interface CacheLayer {
  id: string
  name: string
  ttl: string
  business: string
  policy: string
  where: string
}

const layers: CacheLayer[] = [
  {
    id: 'l1',
    name: 'L1 — CDN / Page HTTP',
    ttl: 'no-store or 300s',
    where: '/s GSSP response headers',
    business:
      'Search pages with live SSR results are never shared at the CDN. Empty shells may be cached briefly.',
    policy: 'private, no-store (SSR data) · public, s-maxage=300 (no data)',
  },
  {
    id: 'l2',
    name: 'L2 — Browser SWR',
    ttl: 'Session memory',
    where: 'FastStore React hooks',
    business:
      'Prevents skeleton flashes after the first load. Header typeahead warms the cache before navigating to search.',
    policy: 'In-memory; seeded from SSR payload + client fetches',
  },
  {
    id: 'l3',
    name: 'L3 — Storefront server',
    ttl: 'Per request',
    where: 'GraphQL resolver + unbxdBridge.ts',
    business:
      'Protects the bridge under load. Duplicate in-flight requests are deduplicated; repeated failures open a circuit breaker.',
    policy: 'Inflight dedup · circuit breaker 5 fails / 30s cooldown',
  },
  {
    id: 'l4',
    name: 'L4 — Bridge worker memory',
    ttl: '30 seconds',
    where: 'VTEX IO worker (Unbxd + Catalog clients)',
    business:
      'Identical anonymous requests within 30 seconds reuse upstream responses inside the bridge worker.',
    policy: 'CacheType.Memory, forceMaxAge: 30 (PUBLIC policy only)',
  },
  {
    id: 'l5',
    name: 'L5 — Bridge DTO policy',
    ttl: '60s or 0',
    where: 'chooseCachePolicy() in bridge',
    business:
      'Logged-in shoppers, regional pricing, or QA overrides always get fresh results. Anonymous default visitors may see 60s cached API responses.',
    policy: 'PUBLIC 60s (anonymous) · NO_STORE (cookies / region / QA)',
  },
]

/**
 * Renders the interactive five-layer cache visualization.
 */
export function renderCacheLayers(): string {
  const cards = layers
    .map(
      (l, i) =>
        `<button type="button" class="cache-layers__card${i === 0 ? ' is-active' : ''}" data-cache-layer="${l.id}" aria-pressed="${i === 0}">
          <div class="cache-layers__header">
            <h4 class="cache-layers__name">${l.name}</h4>
            <span class="cache-layers__ttl">${l.ttl}</span>
          </div>
        </button>`
    )
    .join('')

  const first = layers[0]
  const detail = `<div class="cache-layers__detail" data-cache-detail>
    <h4 class="cache-layers__detail-title" data-cache-detail-title>${first.name}</h4>
    <p class="cache-layers__detail-text" data-cache-detail-business>${first.business}</p>
    <p class="cache-layers__detail-text" data-cache-detail-where><strong>Where:</strong> ${first.where}</p>
    <pre class="cache-layers__policy" data-cache-detail-policy>${first.policy}</pre>
  </div>`

  return `<div class="cache-layers" data-component="cache-layers">
    <div class="cache-layers__stack" role="group" aria-label="Cache layers">${cards}</div>
    ${detail}
  </div>`
}

/**
 * Initializes cache layer card interactions.
 */
export function initCacheLayers(root: ParentNode = document): void {
  const container = root.querySelector('[data-component="cache-layers"]')
  if (!container) return

  const cards = container.querySelectorAll<HTMLButtonElement>('[data-cache-layer]')
  const titleEl = container.querySelector('[data-cache-detail-title]')
  const businessEl = container.querySelector('[data-cache-detail-business]')
  const whereEl = container.querySelector('[data-cache-detail-where]')
  const policyEl = container.querySelector('[data-cache-detail-policy]')

  cards.forEach((card) => {
    card.addEventListener('click', () => {
      const id = card.dataset.cacheLayer
      const layer = layers.find((l) => l.id === id)
      if (!layer) return

      cards.forEach((c) => {
        const active = c === card
        c.classList.toggle('is-active', active)
        c.classList.toggle('is-dimmed', !active)
        c.setAttribute('aria-pressed', String(active))
      })

      if (titleEl) titleEl.textContent = layer.name
      if (businessEl) businessEl.textContent = layer.business
      if (whereEl) whereEl.innerHTML = `<strong>Where:</strong> ${layer.where}`
      if (policyEl) policyEl.textContent = layer.policy
    })
  })
}
